/**
 * postMessage protocol with the DockPro embed in preview mode.
 * Mirrors packages/embed/src/preview/usePreviewMode.js in the
 * DockPro (embed) repo.
 *
 *   Embed → portal:  { type: "embed-ready" }
 *   Portal → embed:  { type: "load-design", payload: <designState> }
 *
 * Round 4 — live-edit capstone. Additive messages, sent only when the
 * iframe was built with `buildEmbedUrl(tenantId, { edit: true })` (i.e.
 * `?preview=1&edit=1` — the embed's editable surface):
 *
 *   Embed → portal:  { type: "design-loaded" }
 *   Portal → embed:  { type: "request-snapshot", requestId: string }
 *   Embed → portal:  { type: "design-snapshot", requestId: string, payload: <designState> }
 *
 * `design-loaded` matters because `embed-ready` only means "the iframe
 * mounted" — NOT "the lead's actual design has been applied yet" (that
 * still requires a `load-design` round trip and the embed's own
 * rehydration). Gate the Save affordance on having received
 * `design-loaded` for the CURRENT edit session, not on `embed-ready`
 * alone — see the embed repo's usePreviewMode.js for the full rationale
 * (a save issued before this arrives risks capturing the embed's
 * starter template instead of the lead's real design).
 */

export interface EmbedReadyMessage {
  type: "embed-ready";
}

export interface LoadDesignMessage {
  type: "load-design";
  payload: unknown; // designState from the lead
}

export interface DesignLoadedMessage {
  type: "design-loaded";
}

export interface RequestSnapshotMessage {
  type: "request-snapshot";
  requestId: string;
}

export interface DesignSnapshotMessage {
  type: "design-snapshot";
  requestId?: string;
  payload: unknown;
}

export function isEmbedReady(data: unknown): data is EmbedReadyMessage {
  return typeof data === "object" && data !== null
         && (data as { type?: string }).type === "embed-ready";
}

export function isDesignLoaded(data: unknown): data is DesignLoadedMessage {
  return typeof data === "object" && data !== null
         && (data as { type?: string }).type === "design-loaded";
}

export function isDesignSnapshot(data: unknown): data is DesignSnapshotMessage {
  return typeof data === "object" && data !== null
         && (data as { type?: string }).type === "design-snapshot";
}

/**
 * Build the iframe URL for a tenant. The embed will load in
 * preview mode and announce readiness via postMessage when
 * ready to receive the design.
 *
 * `opts.edit` (Round 4 — live-edit capstone): when true, adds `edit=1`
 * so the embed mounts its EDITABLE surface (real edit affordances live,
 * plus the request-snapshot/design-snapshot/design-loaded messages
 * above) instead of the always-read-only viewer. Omitting `opts`
 * entirely preserves the exact pre-existing read-only URL (every
 * existing call site is unaffected).
 *
 * `opts.author` (round8/template-authoring): when true — and only
 * meaningful ALONGSIDE `edit` — adds `author=1`, which makes the embed
 * treat the session as a builder authoring a reusable template rather
 * than a homeowner designing or a builder re-editing a homeowner's
 * saved lead. Concretely (see the embed's own DockDesigner
 * `suppressHomeownerUI` prop) it suppresses the live lead-capture CTA
 * and its modal, the homeowner first-run onboarding modal and guided
 * tour, and the autosave restore banner.
 *
 * The lead-capture suppression is the load-bearing one: on the plain
 * `edit=1` surface that CTA is live and wired to a real POST /api/submit,
 * so without `author=1` a builder drawing a template could file a
 * genuine lead into their own tenant and consume their monthly submit
 * quota. Never mount the template-authoring surface without it.
 *
 * `author` without `edit` is inert (the embed only consults it once
 * `preview=1&edit=1` already hold), but this helper still only emits it
 * alongside `edit` so the URL never implies a mode the embed won't honor.
 */
export function buildEmbedUrl(
  tenantId: string,
  opts: { edit?: boolean; author?: boolean } = {},
): string {
  const base = import.meta.env.VITE_EMBED_URL || "";
  if (!base) {
    throw new Error("VITE_EMBED_URL is not configured");
  }
  const url = new URL(base);
  url.searchParams.set("builder", tenantId);
  url.searchParams.set("preview", "1");
  if (opts.edit) {
    url.searchParams.set("edit", "1");
    // Only alongside `edit` — see this function's doc comment.
    if (opts.author) {
      url.searchParams.set("author", "1");
    }
  }
  return url.toString();
}

/**
 * Extract the origin from VITE_EMBED_URL for postMessage
 * origin validation.
 */
export function getEmbedOrigin(): string {
  const base = import.meta.env.VITE_EMBED_URL || "";
  if (!base) return "";
  return new URL(base).origin;
}
